"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  lowercaseWords,
  wordLength,
} from "@/lib/tool-kit/text-analysis/tokens";

/**
 * The word counter's interactive half: the textarea, the eight statistics and the
 * frequency list. Everything above and below it — the H1, the FAQ, the related links —
 * is prose and lives in `src/content/tools/word-counter/en.json`.
 *
 * The counting rules are unchanged from the hand-written page. The two rates below used
 * to contradict the FAQ, which claimed 238 wpm reading and 150 speaking; the copy was
 * corrected to the rates the tool actually divides by rather than the reverse, because a
 * page must not publish a number its own arithmetic does not use. Changing the constants
 * instead would have silently moved every estimate the tool has ever produced.
 *
 * `/tools/reading-time-calculator` divides by 238 (Brysbaert 2019) and says so. The two
 * pages therefore quote different reading speeds; each is honest about its own, which is
 * the property that matters here, but the divergence is a real product question.
 */

const RE_WHITESPACE = /\s/g;
const RE_SPLIT_WORDS = /\s+/;
const RE_SPLIT_SENTENCES = /[.!?]+/;
const RE_SPLIT_PARAGRAPHS = /\n\s*\n/;

const STOP_WORDS = new Set([
  "the",
  "a",
  "an",
  "and",
  "or",
  "but",
  "in",
  "on",
  "at",
  "to",
  "for",
  "of",
  "with",
  "by",
  "from",
  "is",
  "it",
  "this",
  "that",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "have",
  "has",
  "had",
  "do",
  "does",
  "did",
  "will",
  "would",
  "could",
  "should",
  "may",
  "might",
  "shall",
  "can",
  "not",
  "no",
  "so",
  "if",
  "as",
  "its",
  "my",
  "your",
  "his",
  "her",
  "our",
  "their",
  "we",
  "you",
  "he",
  "she",
  "they",
  "me",
  "him",
  "us",
  "them",
  "i",
  "am",
  "what",
  "which",
  "who",
  "whom",
  "how",
  "when",
  "where",
  "why",
  "all",
  "each",
  "every",
  "both",
  "few",
  "more",
  "most",
  "other",
  "some",
  "such",
  "than",
  "too",
  "very",
  "just",
  "about",
]);

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_COUNTER_STRINGS = {
  inputLabel: "Text to count words in",
  placeholder: "Paste or type your text here...",
  words: "Words",
  characters: "Characters",
  charactersNoSpaces: "Characters (no spaces)",
  sentences: "Sentences",
  paragraphs: "Paragraphs",
  pages: "Pages (250 w/p)",
  readingTime: "Reading Time",
  speakingTime: "Speaking Time",
  topWordsHeading: "Top 10 Most Frequent Words",
  seconds: "sec",
  minutes: "min",
};

type Strings = typeof WORD_COUNTER_STRINGS;

interface TextStats {
  words: number;
  characters: number;
  charactersNoSpaces: number;
  sentences: number;
  paragraphs: number;
  pages: number;
  readingTime: string;
  speakingTime: string;
}

function formatTime(minutes: number, strings: Strings): string {
  if (minutes < 1) {
    return `${Math.ceil(minutes * 60)} ${strings.seconds}`;
  }
  const m = Math.floor(minutes);
  const s = Math.round((minutes - m) * 60);
  if (s === 0) {
    return `${m} ${strings.minutes}`;
  }
  return `${m} ${strings.minutes} ${s} ${strings.seconds}`;
}

function computeStats(text: string, strings: Strings): TextStats {
  if (text.trim().length === 0) {
    return {
      words: 0,
      characters: 0,
      charactersNoSpaces: 0,
      sentences: 0,
      paragraphs: 0,
      pages: 0,
      readingTime: `0 ${strings.seconds}`,
      speakingTime: `0 ${strings.seconds}`,
    };
  }

  const characters = text.length;
  const charactersNoSpaces = text.replace(RE_WHITESPACE, "").length;
  const wordsArray = text.trim().split(RE_SPLIT_WORDS).filter(Boolean);
  const words = wordsArray.length;
  const sentences = text
    .split(RE_SPLIT_SENTENCES)
    .filter((s) => s.trim().length > 0).length;
  const paragraphs = text
    .split(RE_SPLIT_PARAGRAPHS)
    .filter((p) => p.trim().length > 0).length;
  const pages = Math.ceil(words / 250);

  return {
    words,
    characters,
    charactersNoSpaces,
    sentences,
    paragraphs,
    pages,
    readingTime: formatTime(words / 200, strings),
    speakingTime: formatTime(words / 130, strings),
  };
}

/**
 * The most frequent words, for the top-ten list. These are the words a reader would name
 * if asked to list them (letters from any script, digits, "don't" and "well-known" whole),
 * which is not the same thing as the headline count: that one is "anything between
 * spaces", as the page says, so a URL or a lone dash is one word there and none here.
 */
export function getTopWords(
  text: string,
  count: number
): { word: string; freq: number }[] {
  const words = lowercaseWords(text, { groupedNumbers: true }).filter(
    (w) => wordLength(w) > 1 && !STOP_WORDS.has(w)
  );

  const freq = new Map<string, number>();
  for (const w of words) {
    freq.set(w, (freq.get(w) ?? 0) + 1);
  }

  return [...freq.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, count)
    .map(([word, f]) => ({ word, freq: f }));
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function WordCounterWidget() {
  const strings = useToolUi(WORD_COUNTER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");

  const stats = computeStats(text, strings);
  const topWords = getTopWords(text, 10);

  return (
    <>
      <textarea
        aria-label={strings.inputLabel}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.placeholder}
        value={text}
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label={strings.words} value={fmt(stats.words)} />
        <StatCard label={strings.characters} value={fmt(stats.characters)} />
        <StatCard
          label={strings.charactersNoSpaces}
          value={fmt(stats.charactersNoSpaces)}
        />
        <StatCard label={strings.sentences} value={fmt(stats.sentences)} />
        <StatCard label={strings.paragraphs} value={fmt(stats.paragraphs)} />
        <StatCard label={strings.pages} value={stats.pages} />
        <StatCard label={strings.readingTime} value={stats.readingTime} />
        <StatCard label={strings.speakingTime} value={stats.speakingTime} />
      </div>

      {topWords.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.topWordsHeading}
          </h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {topWords.map((item) => (
              <div
                className="rounded-lg border border-border bg-card px-3 py-2 text-center"
                key={item.word}
              >
                <p className="font-medium text-foreground text-sm">
                  {item.word}
                </p>
                <p className="text-muted-foreground text-xs">
                  {fmt(item.freq)}x
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
